import { FacilityDTO, UserStaffBean, UserStaffPublicDTO } from "@gymflow/types";
import { z } from "zod";

import { AppointableFormValues } from "./types";

export const INITIAL_VALUES_APPOINTABLE_FORM: AppointableFormValues = {
  details: {
    name: "",
    category: undefined,
    duration: undefined,
    blockExtraTime: undefined,
    bookingPossibleEvery: undefined,
    isMembersOnly: false,
    isTopOfTheHour: false,
    description: "",
    appointmentTerms: "",
  },
  pricingStructure: {
    pricingStructure: "FIXED_PRICE",
    isUpfrontPaymentRequired: true,
    typeOfCost: "PRICE",
    credits: 0,
    price: {
      exTaxPrice: 0,
      incTaxPrice: 0,
      salesTaxRate: 0,
    },
    isShowMembersPrice: false,
    membersPrice: {
      exTaxPrice: 0,
      incTaxPrice: 0,
    },
  },
  resources: {
    resourceRequired: "STAFF",
    appointmentHostsStaff: [],
    appointmentHostsFacility: [],
    otherResourcesRequired: "NO",
    otherResourcesFacility: [],
    otherResourcesStaff: [],
  },
};

export const appointableFormSchema = z.object({
  details: z
    .object({
      name: z.string().max(100, "Maximum 100 characters").optional(),
      category: z
        .object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<number>(),
        })
        .optional(),
      duration: z
        .object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<number>(),
        })
        .optional(),
      blockExtraTime: z
        .object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<number>(),
        })
        .optional(),
      bookingPossibleEvery: z
        .object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<number>(),
        })
        .optional(),
      isMembersOnly: z.boolean(),
      isTopOfTheHour: z.boolean(),
      description: z.string().max(2000, "Maximum 2000 characters").optional(),
      appointmentTerms: z
        .string()
        .max(500, "Maximum 500 characters")
        .optional(),
    })
    .superRefine((values, ctx) => {
      if (!values.name) {
        ctx.addIssue({
          path: ["name"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (!values.category) {
        ctx.addIssue({
          path: ["category"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (!values.duration) {
        ctx.addIssue({
          path: ["duration"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (!values.bookingPossibleEvery) {
        ctx.addIssue({
          path: ["bookingPossibleEvery"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
    }),
  pricingStructure: z
    .object({
      pricingStructure: z.enum(["FREE", "FIXED_PRICE"]),
      isUpfrontPaymentRequired: z.boolean(),
      typeOfCost: z.enum(["PRICE", "CREDITS", "BOTH"]),
      credits: z.number().min(0).optional(),
      price: z.object({
        exTaxPrice: z.number().min(0).optional(),
        incTaxPrice: z.number().min(0).optional(),
        salesTaxRate: z.number().min(0).max(100).optional(),
      }),
      isShowMembersPrice: z.boolean(),
      membersPrice: z.object({
        exTaxPrice: z.number().min(0).optional(),
        incTaxPrice: z.number().min(0).optional(),
      }),
    })
    .superRefine((values, ctx) => {
      if (
        values.pricingStructure === "FIXED_PRICE" &&
        ["CREDITS", "BOTH"].includes(values.typeOfCost) &&
        values.credits === undefined
      ) {
        ctx.addIssue({
          path: ["credits"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (
        values.pricingStructure === "FIXED_PRICE" &&
        ["PRICE", "BOTH"].includes(values.typeOfCost)
      ) {
        if (values.price.exTaxPrice === undefined) {
          ctx.addIssue({
            path: ["price.exTaxPrice"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }
        if (values.price.incTaxPrice === undefined) {
          ctx.addIssue({
            path: ["price.incTaxPrice"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }
        if (values.price.salesTaxRate === undefined) {
          ctx.addIssue({
            path: ["price.salesTaxRate"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }
        if (values.isShowMembersPrice) {
          if (values.membersPrice.exTaxPrice === undefined) {
            ctx.addIssue({
              path: ["membersPrice.exTaxPrice"],
              code: z.ZodIssueCode.custom,
              message: "This field is required",
            });
          }
          if (values.membersPrice.incTaxPrice === undefined) {
            ctx.addIssue({
              path: ["membersPrice.incTaxPrice"],
              code: z.ZodIssueCode.custom,
              message: "This field is required",
            });
          }
        }
      }
    }),
  resources: z
    .object({
      resourceRequired: z.enum(["STAFF", "FACILITY"]),
      appointmentHostsStaff: z.array(
        z.object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<UserStaffBean | UserStaffPublicDTO>(),
        }),
      ),
      appointmentHostsFacility: z.array(
        z.object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<FacilityDTO>(),
        }),
      ),
      otherResourcesRequired: z.enum(["YES", "NO"]),
      otherResourcesFacility: z.array(
        z.object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<FacilityDTO>(),
        }),
      ),
      otherResourcesStaff: z.array(
        z.object({
          id: z.union([z.string(), z.number()]),
          label: z.string(),
          value: z.custom<UserStaffBean | UserStaffPublicDTO>(),
        }),
      ),
    })
    .superRefine((values, ctx) => {
      if (
        values.resourceRequired === "STAFF" &&
        values.appointmentHostsStaff.length === 0
      ) {
        ctx.addIssue({
          path: ["appointmentHostsStaff"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (
        values.resourceRequired === "FACILITY" &&
        values.appointmentHostsFacility.length === 0
      ) {
        ctx.addIssue({
          path: ["appointmentHostsFacility"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      if (values.otherResourcesRequired === "YES") {
        if (
          values.resourceRequired === "FACILITY" &&
          values.otherResourcesStaff.length === 0
        ) {
          ctx.addIssue({
            path: ["otherResourcesStaff"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }
        if (
          values.resourceRequired === "STAFF" &&
          values.otherResourcesFacility.length === 0
        ) {
          ctx.addIssue({
            path: ["otherResourcesFacility"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }
      }
    }),
});
